import { Thumbnail, token } from '@bynder/design-system';
import { useCustomCSSEditor } from '../../../../../hooks/useCustomCSSEditor';
import { SettingsAccordion } from '../../SettingsAccordion';
import { IconListNumbered } from '@bynder/icons';
import InspectorPanelColorPicker from '../../ColorPicker';
import {
  CSS_CLASSES,
  CSS_PROPERTIES,
  INSPECTOR_DEBOUNCE_DELAY,
} from '../../constants';
import {
  useActiveThemeset,
  useGetThemesetColors,
} from '../../../../../queries/themesets';
import { useTranslate } from '@bynder/localization';
import { defaultTextColor } from '../../../../widgets/RichText/Config';
import { useTheme } from 'styled-components';
import { getFontStyleByWeight } from '../../../../shared/styled-components/constants';
import { useDebouncedCallback } from 'use-debounce';
import { useWidgetStyleProperty } from '../../helpers';
import InspectorTypoPicker, {
  TypographyChoice,
} from '../../InspectorTypoPicker';

const ColorPaletteValue = ({
  style,
  handleStyleChange,
}: {
  style: string;
  handleStyleChange: (value: string) => void;
}) => {
  const { translate } = useTranslate();
  const {
    addTypographyToStyle,
    findColorFromStyle,
    clearStyle,
    addColorToStyle,
    addValueToStyle,
  } = useCustomCSSEditor(style);
  const { data: themeset } = useActiveThemeset();
  const customColors = [...useGetThemesetColors(), defaultTextColor];
  const theme = useTheme();

  const handleTypographyPropertyChange = newValue => {
    const newStyle = addTypographyToStyle(
      theme,
      newValue,
      CSS_CLASSES.COLOR_PALETTE_VALUE,
    );
    handleStyleChange(newStyle);
  };

  const handleClear = (property, selector?) => {
    const newStyle = clearStyle(property, selector);
    handleStyleChange(newStyle);
  };

  const debouncedSetWidgetStyle = useDebouncedCallback(
    handleStyleChange,
    INSPECTOR_DEBOUNCE_DELAY,
  );

  const handleFontSizeChange = (size: number) => {
    const newStyle = addValueToStyle(
      size,
      CSS_PROPERTIES.FONT_SIZE,
      CSS_CLASSES.COLOR_PALETTE_VALUE,
    );
    debouncedSetWidgetStyle(newStyle);
  };

  const handleClick = (colorId, property, selector?) => {
    const newStyle = addColorToStyle(colorId, property, selector);
    handleStyleChange(newStyle);
  };

  const defaultCardTextColor =
    themeset?.widgets.colorPalette.value?.textColor?.hexCode || token.colorText;

  const { value: fontSize, origin: fontSizeOrigin } = useWidgetStyleProperty(
    style,
    'widgets.colorPalette.value.fontSize',
    CSS_PROPERTIES.FONT_SIZE,
    CSS_CLASSES.COLOR_PALETTE_VALUE,
  );

  const { value: fontFamily, origin: fontFamilyOrigin } =
    useWidgetStyleProperty(
      style,
      'widgets.colorPalette.value.fontFamily',
      CSS_PROPERTIES.FONT_FAMILY,
      CSS_CLASSES.COLOR_PALETTE_VALUE,
    );

  const { value: fontWeight, origin: fontWeightOrigin } =
    useWidgetStyleProperty(
      style,
      'widgets.colorPalette.value.fontStyle',
      CSS_PROPERTIES.FONT_WEIGHT,
      CSS_CLASSES.COLOR_PALETTE_VALUE,
    );

  const fontStyle = getFontStyleByWeight(theme, fontFamily, fontWeight);

  return (
    <SettingsAccordion
      noDivider
      thumbnail={<Thumbnail icon={<IconListNumbered />} />}
      title="Value"
    >
      <InspectorTypoPicker
        fontSize={parseInt(fontSize, 10)}
        fontFamily={fontFamily}
        fontFamilyOrigin={fontFamilyOrigin}
        fontSizeOrigin={fontSizeOrigin}
        fontStyleOrigin={fontWeightOrigin}
        fontStyle={fontStyle}
        handleFontChange={(newTypo: TypographyChoice) => {
          handleTypographyPropertyChange(newTypo);
        }}
        handleSizeChange={handleFontSizeChange}
        canEdit
      />

      <InspectorPanelColorPicker
        handleClick={colorId => {
          handleClick(
            colorId,
            CSS_PROPERTIES.COLOR,
            CSS_CLASSES.COLOR_PALETTE_VALUE,
          );
        }}
        handleClear={() => {
          handleClear(CSS_PROPERTIES.COLOR, CSS_CLASSES.COLOR_PALETTE_VALUE);
        }}
        colors={customColors}
        activeColor={findColorFromStyle(
          CSS_PROPERTIES.COLOR,
          CSS_CLASSES.COLOR_PALETTE_VALUE,
        )}
        defaultColorHex={defaultCardTextColor}
        label={translate('WIDGET_INSPECTOR.CARD_COLOR_TITLE')}
      />
    </SettingsAccordion>
  );
};
export default ColorPaletteValue;
